import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
  MdSearch, MdClose, MdArticle, MdTune, MdPeople, MdGroups,
  MdComment, MdLocationOn, MdAutoAwesome, MdHandshake, MdFormatQuote, MdChevronRight, MdShield
} from 'react-icons/md';

// Helper function to highlight matching keywords
function HighlightMatch({ text = '', match = '' }) {
  if (!match || !text) return <span>{text}</span>;
  const parts = String(text).split(new RegExp(`(${match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === match.toLowerCase() ? (
          <mark key={i} style={{ backgroundColor: '#FEF08A', color: '#0F172A', padding: '0 2px', borderRadius: '2px', fontWeight: 700 }}>
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </span>
  );
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search trigger (300ms)
  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      executeSearch(query.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const executeSearch = async (searchTerm) => {
    if (!searchTerm) return;
    setLoading(true);
    setIsOpen(true);
    try {
      const res = await api.get('/admin/search', { params: { q: searchTerm } });
      if (res.data?.success) {
        setResults(res.data);
      }
    } catch (err) {
      console.error('Global search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch(query.trim());
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleSelectResult = (targetPath) => {
    setIsOpen(false);
    navigate(targetPath);
  };

  const currentKeyword = results?.query || query;
  const hasArticles = results?.data?.articles?.length > 0;
  const hasUsers = results?.data?.users?.length > 0;
  const hasContributors = results?.data?.contributors?.length > 0;
  const hasComments = results?.data?.comments?.length > 0;
  const hasQuotes = results?.data?.quotes?.length > 0;
  const hasStatusTemplates = results?.data?.statusTemplates?.length > 0;
  const hasAdmins = results?.data?.admins?.length > 0;
  const hasCategories = results?.data?.categories?.length > 0;
  const hasLocations = results?.data?.locations?.length > 0;
  const hasPartners = results?.data?.partners?.length > 0;

  const totalFound = results?.total || 0;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%', maxWidth: '420px' }}>
      <div className="topbar-search" style={{ position: 'relative', width: '100%' }}>
        <MdSearch className="search-icon" style={{ cursor: 'pointer' }} onClick={() => executeSearch(query.trim())} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (query.trim() && results) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Global Search (Articles, Users, Comments, Quotes...)"
          style={{ width: '100%', paddingRight: query ? '32px' : '12px' }}
        />
        {query && (
          <MdClose
            onClick={() => { setQuery(''); setResults(null); setIsOpen(false); }}
            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#9CA3AF', fontSize: '18px' }}
          />
        )}
      </div>

      {isOpen && (query.trim() || loading) && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #E2E8F0',
          maxHeight: '480px',
          overflowY: 'auto',
          zIndex: 1000,
          padding: '12px 0'
        }}>
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '14px' }}>
              🔍 Searching across all admin modules...
            </div>
          ) : totalFound === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#64748B' }}>
              <div style={{ fontSize: '28px', marginBottom: '6px' }}>🧐</div>
              <p style={{ margin: 0, fontWeight: 600, color: '#1E293B' }}>No results found for "{query}"</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#94A3B8' }}>Try searching with different keywords</p>
            </div>
          ) : (
            <div>
              <div style={{ padding: '4px 16px 8px 16px', fontSize: '11.5px', fontWeight: 700, color: '#64748B', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between' }}>
                <span>GLOBAL SEARCH RESULTS FOR "{currentKeyword}"</span>
                <span>{totalFound} match{totalFound === 1 ? '' : 'es'}</span>
              </div>

              {/* Articles & Review Articles */}
              {hasArticles && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#2563EB', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdArticle size={14} /> ARTICLES ({results.data.articles.length})
                  </div>
                  {results.data.articles.map(art => (
                    <div
                      key={art.id}
                      onClick={() => handleSelectResult(art.status === 'draft' ? `/review-articles?search=${encodeURIComponent(art.title)}` : `/articles?search=${encodeURIComponent(art.title)}&id=${art.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                      className="global-search-item"
                    >
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <HighlightMatch text={art.title} match={currentKeyword} />
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                          {art.category || 'News'} • {art.city || 'All India'} • Status: <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{art.status}</span>
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8', flexShrink: 0 }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Users */}
              {hasUsers && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdPeople size={14} /> USERS ({results.data.users.length})
                  </div>
                  {results.data.users.map(u => (
                    <div
                      key={u.id}
                      onClick={() => handleSelectResult(`/users?search=${encodeURIComponent(u.name || u.email || u.phone || '')}&id=${u.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          <HighlightMatch text={u.name || 'User #' + u.id} match={currentKeyword} />
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                          <HighlightMatch text={u.email || u.phone || 'No contact info'} match={currentKeyword} />
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Contributors & Contributor News */}
              {hasContributors && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#7C3AED', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdGroups size={14} /> CONTRIBUTORS ({results.data.contributors.length})
                  </div>
                  {results.data.contributors.map(c => (
                    <div
                      key={c.id}
                      onClick={() => handleSelectResult(`/contributors?search=${encodeURIComponent(c.name || c.phone || c.email || '')}&id=${c.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          <HighlightMatch text={c.name} match={currentKeyword} />
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                          <HighlightMatch text={c.phone || c.email || 'N/A'} match={currentKeyword} />
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Comments & Moderation */}
              {hasComments && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#DC2626', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdComment size={14} /> COMMENTS ({results.data.comments.length})
                  </div>
                  {results.data.comments.map(cm => (
                    <div
                      key={cm.id}
                      onClick={() => handleSelectResult(`/comments?search=${encodeURIComponent(cm.body || '')}&id=${cm.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          "<HighlightMatch text={cm.body} match={currentKeyword} />"
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                          By: <HighlightMatch text={cm.guest_label || 'User #' + (cm.user_id || 'Guest')} match={currentKeyword} /> {cm.Article ? `on: ${cm.Article.title}` : ''}
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8', flexShrink: 0 }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Quotes */}
              {hasQuotes && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#65A30D', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdFormatQuote size={14} /> QUOTES ({results.data.quotes.length})
                  </div>
                  {results.data.quotes.map(q => (
                    <div
                      key={q.id}
                      onClick={() => handleSelectResult(`/quotes?search=${encodeURIComponent(q.title || q.quote || q.author || '')}&id=${q.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          "<HighlightMatch text={q.title || 'Quote #' + q.id} match={currentKeyword} />"
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Status Templates */}
              {hasStatusTemplates && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#9333EA', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdAutoAwesome size={14} /> STATUS TEMPLATES ({results.data.statusTemplates.length})
                  </div>
                  {results.data.statusTemplates.map(st => (
                    <div
                      key={st.id}
                      onClick={() => handleSelectResult(`/status?search=${encodeURIComponent(st.title || st.tags || '')}&id=${st.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          <HighlightMatch text={st.title || 'Template #' + st.id} match={currentKeyword} />
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                          Lang: {st.language || 'en'} • Tags: <HighlightMatch text={st.tags || 'General'} match={currentKeyword} />
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Admins */}
              {hasAdmins && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#4F46E5', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdShield size={14} /> ADMINS ({results.data.admins.length})
                  </div>
                  {results.data.admins.map(a => (
                    <div
                      key={a.id}
                      onClick={() => handleSelectResult(`/admins?search=${encodeURIComponent(a.name || a.email || '')}&id=${a.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          <HighlightMatch text={a.name} match={currentKeyword} /> ({a.role})
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                          <HighlightMatch text={a.email} match={currentKeyword} />
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Categories */}
              {hasCategories && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#D97706', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdTune size={14} /> CATEGORIES ({results.data.categories.length})
                  </div>
                  {results.data.categories.map(cat => (
                    <div
                      key={cat.id}
                      onClick={() => handleSelectResult(`/manage-categories?search=${encodeURIComponent(cat.name || '')}&id=${cat.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          <HighlightMatch text={cat.name} match={currentKeyword} />
                        </span>
                        <span style={{ fontSize: '11.5px', color: '#64748B', marginLeft: '6px' }}>
                          (<HighlightMatch text={cat.slug} match={currentKeyword} />)
                        </span>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Locations (States & Cities) */}
              {hasLocations && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#0284C7', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdLocationOn size={14} /> LOCATIONS ({results.data.locations.length})
                  </div>
                  {results.data.locations.map((loc, i) => (
                    <div
                      key={i}
                      onClick={() => handleSelectResult(`/manage-locations?search=${encodeURIComponent(loc.name || '')}&id=${loc.id || ''}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          {loc.type === 'state' ? 'State' : 'City'}: <HighlightMatch text={loc.name} match={currentKeyword} />
                        </span>
                        {loc.state && (
                          <span style={{ fontSize: '11.5px', color: '#64748B', marginLeft: '6px' }}>
                            (<HighlightMatch text={loc.state} match={currentKeyword} />)
                          </span>
                        )}
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Partners */}
              {hasPartners && (
                <div style={{ padding: '8px 0' }}>
                  <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 800, color: '#EA580C', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdHandshake size={14} /> PARTNERS ({results.data.partners.length})
                  </div>
                  {results.data.partners.map(p => (
                    <div
                      key={p.id}
                      onClick={() => handleSelectResult(`/partners?search=${encodeURIComponent(p.name || p.partner_code || p.email || '')}&id=${p.id}`)}
                      style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="global-search-item"
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A' }}>
                          <HighlightMatch text={p.name} match={currentKeyword} /> (<HighlightMatch text={p.partner_code} match={currentKeyword} />)
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                          <HighlightMatch text={p.email || p.phone} match={currentKeyword} />
                        </div>
                      </div>
                      <MdChevronRight style={{ color: '#94A3B8' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
